import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectRaw from 'components/Inputs/Select';
import { FORMAT_TYPES } from '__components/Kizen/RangeInput';
import { ResizableSection } from '__components/Wizards/CustomObject/steps/CustomLayout/dialogs/common';
import { StyledRangeInput } from './commonStyles';
import { PluginContext } from 'ts-components/Plugins/context';

const Select = SelectRaw as React.ComponentType<any>;

const RECORD_DETAIL_SURFACE = 'records';

const SLIDER_PARAMS = {
  MIN: 100,
  MAX: 800,
  STEP: 10,
};

const DEFAULT_HEIGHT = 300;

interface PluginBlockRecordWizardProps {
  index?: number;
  isActive?: boolean;
  pluginApiName: string;
  metadata: Record<string, any>;
  setMetadata: (meta: Record<string, any>, dirty?: boolean) => void;
  [key: string]: unknown;
}

export const PluginBlockRecordWizard = ({
  isActive,
  pluginApiName,
  metadata,
  setMetadata,
  ...props
}: PluginBlockRecordWizardProps) => {
  const { t } = useTranslation();
  const { pluginBlocks, businessPluginAppsByApiName } = useContext(PluginContext);

  const pluginName = businessPluginAppsByApiName[pluginApiName]?.plugin_app?.name;

  const blockOptions = useMemo(() => {
    const blockEntry = pluginBlocks.find(
      (p) => p.plugin_api_name === pluginApiName
    );
    return (blockEntry?.blocks ?? [])
      .filter(
        (block) =>
          !block.types?.length || block.types.includes(RECORD_DETAIL_SURFACE)
      )
      .map((block: any) => ({
        value: block.api_name,
        label: block.name,
        block,
      }));
  }, [pluginBlocks, pluginApiName]);

  const selectedBlock = useMemo(() => {
    return blockOptions.find((o) => o.value === metadata?.blockApiName) ?? null;
  }, [blockOptions, metadata?.blockApiName]);

  const currentHeight: number =
    metadata?.recommendedHeight ?? DEFAULT_HEIGHT;

  const handleBlockChange = (option: any) => {
    setMetadata({
      ...metadata,
      pluginApiName,
      blockApiName: option?.value,
      blockName: option?.label,
      recommendedHeight: option?.block?.recommended_height ?? DEFAULT_HEIGHT,
    });
  };

  const handleHeightChange = (height: number) => {
    setMetadata({
      ...metadata,
      recommendedHeight: height,
    });
  };

  return (
    <ResizableSection
      header={pluginName ? t('Configure {{name}} Block', { name: pluginName }) : t('Choose Block')}
      isActive={isActive}
      {...props}
    >
      <div className="kds grid grid-cols-1 gap-y-spacer-20">
        <Select
          className="modal-select"
          label={t('Choose Block')}
          options={blockOptions}
          value={selectedBlock}
          onChange={handleBlockChange}
          placeholder={t('Select a block...')}
          data-qa-id="plugin-block-record-choose-block"
        />
        <StyledRangeInput
          label={t('Block Height')}
          value={currentHeight}
          min={SLIDER_PARAMS.MIN}
          max={SLIDER_PARAMS.MAX}
          step={SLIDER_PARAMS.STEP}
          onChange={handleHeightChange}
          tooltipValue={currentHeight}
          onTooltipChange={handleHeightChange}
          formatMarker={FORMAT_TYPES.PIXELS}
        />
      </div>
    </ResizableSection>
  );
};
